import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabase";
import { PasswordPrompt } from "@/components/PasswordPrompt";
import { StickyNote, Users, ArrowRight, Sparkles, PenTool, Zap, Palette, Code, PenLine, Lock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Contact from "./Contact";

// Function to hash password using Web Crypto API
async function hashPassword(password: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const Index = () => {
    const navigate = useNavigate();

  const grantAccessAndNavigate = (targetRoomId: string, targetUsername: string) => {
    const accessToken = crypto.randomUUID();
    const ticket = { token: accessToken, username: targetUsername };
    // Use a key specific to the room to store the one-time access token, now including the username
    sessionStorage.setItem(`room_access_token_${targetRoomId}`, JSON.stringify(ticket));
    navigate(`/app?room=${targetRoomId}&user=${targetUsername}`);
  };
  const [activeTab, setActiveTab] = useState<'join' | 'create'>('join');
  const [roomId, setRoomId] = useState('');
  const [username, setUsername] = useState('');
  const [roomName, setRoomName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [pendingRoomId, setPendingRoomId] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [sketches] = useState([
    { id: 1, text: 'Brainstorm', x: 20, y: 30, rotate: -5 },
    { id: 2, text: 'Wireframe', x: 70, y: 60, rotate: 3 },
    { id: 3, text: 'Flowchart', x: 40, y: 120, rotate: -2 },
  ]);

  useEffect(() => {
    // Add a subtle animation to the sketch elements
    const interval = setInterval(() => {
      setIsHovered(prev => !prev);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    sessionStorage.setItem('snapspace_username', username.trim());
    if (!username.trim()) {
      setError('Username is required.');
      return;
    }

    if (activeTab === 'create') {
      if (!roomName.trim()) {
        setError('Room name is required.');
        return;
      }
      if (password && password.length < 4) {
        setError('Password must be at least 4 characters long.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }

      // Check for unique room name
      const { data: existing, error: checkError } = await supabase
        .from("rooms")
        .select('id')
        .eq('name', roomName.trim())
        .maybeSingle();

      if (checkError) {
        setError('Error checking room name uniqueness.');
        return;
      }
      if (existing) {
        setError('Room name already exists. Please choose another.');
        return;
      }

      // Create a new room in Supabase
      try {
        const passwordHash = password ? await hashPassword(password) : null;
        
        const { data, error } = await supabase
          .from("rooms")
          .insert([{ 
            name: roomName.trim(), 
            password_hash: passwordHash,
            creator_name: username.trim(),
          }])
          .select('id')
          .single();

        if (error) {
          setError(`Error creating room: ${error.message}`);
          return;
        }
        
        const newRoomId = data.id.toString();

        // Add/update the creator in the participants table as an admin.
        const { error: participantError } = await supabase
          .from('room_participants')
          .upsert({
            room_id: newRoomId,
            participant_name: username.trim(),
            is_admin: true, // Ensure the creator is always an admin
          }, {
            onConflict: 'room_id,participant_name' // Specify conflict target
          });

        if (participantError) {
          // Attempt to clean up the created room if participant insertion fails
          await supabase.from('rooms').delete().eq('id', newRoomId);
          setError(`Failed to add participant: ${participantError.message}`);
          return;
        }

        // Generate and store the session token for the creator.
        const sessionToken = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now();
        localStorage.setItem(`session_token_${newRoomId}`, sessionToken);
        grantAccessAndNavigate(newRoomId, username.trim());
      } catch (err) {
        console.error('Error creating room:', err);
        setError('An unexpected error occurred. Please try again.');
      }
    }

    if (activeTab === 'join') {
        if (!roomId.trim()) {
            setError('Room ID is required.');
            return;
        }
      try {
        const { data: room, error } = await supabase
          .from('rooms')
          .select('id, password_hash')
          .eq('id', roomId.trim())
          .single();

        if (error || !room) {
          setError('Room not found.');
          return;
        }

        if (!room.password_hash) {
          // Public room: add user to participants if they don't exist.
          const { error: participantError } = await supabase
            .from('room_participants')
            .upsert({
              room_id: roomId.trim(),
              participant_name: username.trim(),
              is_admin: false, // Default for new participants
            }, {
              onConflict: 'room_id,participant_name',
              ignoreDuplicates: true // Don't update if user already exists
            });

          if (participantError) {
            setError('Failed to join the room. Please try again.');
            return;
          }

          const sessionToken = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now();
          localStorage.setItem(`session_token_${roomId.trim()}`, sessionToken);
          
          grantAccessAndNavigate(roomId.trim(), username.trim());
        } else {
          // Private room: show password prompt.
          setPendingRoomId(roomId.trim());
          setShowPasswordPrompt(true);
        }
      } catch (err) {
        setError('Error finding room.');
      }
    }
  };

  const handlePasswordConfirm = async (enteredPassword: string) => {
    if (!pendingRoomId) return;

    try {
      const { data: room, error } = await supabase
        .from('rooms')
        .select('password_hash')
        .eq('id', pendingRoomId)
        .single();

      if (error || !room) {
        setPasswordError('Room not found.');
        return;
      }

      const enteredPasswordHash = await hashPassword(enteredPassword);
      if (enteredPasswordHash !== room.password_hash) {
        setPasswordError('Incorrect password.');
        return;
      }
      
      // Password is correct. Add user to participants if they don't exist.
      const { error: participantError } = await supabase
        .from('room_participants')
        .upsert({
          room_id: pendingRoomId,
          participant_name: username.trim(),
          is_admin: false, // Default for new participants
        }, {
          onConflict: 'room_id,participant_name',
          ignoreDuplicates: true // Don't update if user already exists
        });

      if (participantError) {
        setError('Failed to join the room. Please try again.');
        setShowPasswordPrompt(false);
        return;
      }

      const sessionToken = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now();
      localStorage.setItem(`session_token_${pendingRoomId}`, sessionToken);

      setShowPasswordPrompt(false);
      grantAccessAndNavigate(pendingRoomId, username.trim());
    } catch (err) {
      console.error('Error during password confirmation:', err);
      setError('An error occurred while joining the room.');
      setShowPasswordPrompt(false);
    }
  };

  const sketchVariants = {
    initial: { y: 0, rotate: 0 },
    hover: { 
      y: -5, 
      rotate: 2,
      transition: { 
        type: 'spring', 
        stiffness: 300,
        damping: 10
      } 
    }
  };

  return (
    <div className="min-h-screen w-full bg-white text-gray-900 flex flex-col items-center p-4 font-sans overflow-y-auto">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {sketches.map((sketch) => (
          <motion.div
            key={sketch.id}
            className="absolute text-gray-200 text-xs font-mono border border-dashed border-gray-200 px-1.5 py-0.5 rounded-md"
            style={{
              left: `${sketch.x}%`,
              top: `${sketch.y}px`,
              transform: `rotate(${sketch.rotate}deg)`,
            }}
            animate={isHovered ? 'hover' : 'initial'}
            variants={sketchVariants}
            transition={{ duration: 0.5 }}
          >
            {sketch.text}
          </motion.div>
        ))}
      </div>

      <div className="w-full max-w-5xl flex-1 flex flex-col py-8">
        {/* Header */}
        <header className="py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
              <StickyNote className="w-4 h-4" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Made For Teams</h1>
          </div>
          <nav className="flex items-center">
            <Button
              variant="outline"
              size="sm"
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg border border-gray-200 hover:border-gray-300 bg-white/50 backdrop-blur-sm whitespace-nowrap"
              onClick={() => navigate('/contact')}
            >
              Contact Us
            </Button>
          </nav>
        </header>

        <main className="flex-1 flex flex-col justify-start py-8">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              Where ideas take <span className="relative">
                shape
                <svg className="absolute -bottom-1 left-0 w-full h-2.5 text-yellow-300" viewBox="0 0 200 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0,10 Q50,0 100,10 T200,10" fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round" strokeDasharray="0,1" />
                </svg>
              </span>
            </motion.h1>
            <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">
              A collaborative whiteboard that combines organization with creative freedom.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <Button 
                size="default" 
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-4 text-sm font-medium rounded-lg shadow-md hover:shadow transition-all"
                onClick={() => setActiveTab('create')}
              >
                <Zap className="w-4 h-4 mr-2" />
                Create Room - it's free
              </Button>
              <Button 
                variant="outline" 
                size="default" 
                className="px-6 py-4 text-sm font-medium rounded-lg border border-gray-200 hover:border-gray-300 bg-white/50 backdrop-blur-sm"
                onClick={() => setActiveTab('join')}
              >
                <Users className="w-4 h-4 mr-2" />
                Join a room
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border border-gray-200 bg-white/70 backdrop-blur-sm rounded-xl overflow-hidden shadow-sm">
              <CardHeader className="pb-2 px-6 pt-4">
                <CardTitle className="text-lg font-semibold text-gray-900">
                  {activeTab === 'join' ? 'Join a Room' : 'Create New Room'}
                </CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  {activeTab === 'join' ? 'Enter the room code to join your team' : 'Start a new collaboration space'}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-6 pb-4">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {activeTab === 'create' ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="username" className="text-xs font-medium text-gray-700">
                            Your Name
                          </Label>
                          <div className="relative">
                            <Input
                              id="username"
                              type="text"
                              placeholder="Alex Johnson"
                              className="h-10 pl-9 text-sm rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 w-full"
                              value={username}
                              onChange={(e) => setUsername(e.target.value)}
                              required
                            />
                            <PenLine className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="roomName" className="text-xs font-medium text-gray-700">
                            Room Name
                          </Label>
                          <Input
                            id="roomName"
                            type="text"
                            placeholder="e.g., Project Alpha"
                            className="h-10 text-sm rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 w-full"
                            value={roomName}
                            onChange={(e) => setRoomName(e.target.value)}
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Lock className="h-4 w-4" />
                          <Label htmlFor="password">Password (optional)</Label>
                        </div>
                        <Input
                          id="password"
                          type="password"
                          placeholder="Set a password (min 4 chars)"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      {password && (
                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword">Confirm Password</Label>
                          <Input
                            id="confirmPassword"
                            type="password"
                            placeholder="Confirm the password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full"
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-1.5">
                      <Label htmlFor="username" className="text-xs font-medium text-gray-700">
                        Your Name
                      </Label>
                      <div className="relative">
                        <Input
                          id="username"
                          type="text"
                          placeholder="Alex Johnson"
                          className="h-10 pl-9 text-sm rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 w-full"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          required
                        />
                        <PenLine className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="text-xs text-red-600 font-medium pt-1 pl-1">{error}</div>
                  )}

                  <AnimatePresence mode="wait">
                    {activeTab === 'join' && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginBottom: '0.75rem' }}
                        exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                        transition={{ duration: 0.15 }}
                        className="space-y-1.5 overflow-hidden"
                      >
                        <Label htmlFor="roomId" className="text-xs font-medium text-gray-700">
                          Room Code
                        </Label>
                        <div className="relative">
                          <Input
                            id="roomId"
                            type="text"
                            placeholder="e.g., ABC-123-XYZ"
                            className="h-10 pl-9 text-sm rounded-lg border-gray-300 focus:ring-0 focus:ring-offset-0 focus:border-gray-400 bg-white/70"
                            value={roomId}
                            onChange={(e) => setRoomId(e.target.value)}
                            required
                          />
                          <Code className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Button 
                    type="submit"
                    className={`w-full h-11 text-sm font-medium rounded-lg ${
                      activeTab === 'join' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                    }`}
                  >
                    {activeTab === 'join' ? (
                      <>
                        Join Room <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Create New Room <Sparkles className="ml-2 h-3.5 w-3.5" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-4 pt-3 border-t border-gray-100 flex justify-center">
                  <button 
                    onClick={() => setActiveTab(prev => prev === 'join' ? 'create' : 'join')}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {activeTab === 'join' ? 'Or create a new room' : 'Already have a room code?'}
                  </button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <div className="p-1">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Create together, from anywhere</h2>
                <div className="space-y-2">
                  {[
                    {
                      icon: <PenTool className="w-4 h-4 text-blue-500" />,
                      title: "Sketch & Annotate",
                      description: "Draw freely or use templates to visualize your ideas"
                    },
                    {
                      icon: <Users className="w-4 h-4 text-green-500" />,
                      title: "Real-time Collaboration",
                      description: "Work simultaneously with your team"
                    },
                    {
                      icon: <Palette className="w-4 h-4 text-purple-500" />,
                      title: "Beautifully Organized",
                      description: "Keep your work organized"
                    }
                  ].map((feature, index) => (
                    <motion.div 
                      key={index}
                      className="flex items-start space-x-3 p-2.5 rounded-lg hover:bg-gray-50/50 transition-colors cursor-default"
                      whileHover={{ x: 2 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                    >
                      <div className="p-1.5 rounded-md bg-gray-50 mt-0.5">
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{feature.title}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-3 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-500">
            No account required • Works on all devices • Free forever
          </p>
        </footer>
      </div>
      
      {/* Password Prompt Dialog */}
      <PasswordPrompt
        open={showPasswordPrompt}
        onOpenChange={(open) => {
          setShowPasswordPrompt(open);
          if (!open) {
            setPasswordError('');
            setPendingRoomId(null);
          }
        }}
        onConfirm={handlePasswordConfirm}
        error={passwordError}
        title="Join Protected Room"
        description={`Enter the password to join room ${pendingRoomId || ''}`}
      />
    </div>
  );
};

export default Index;
